"use client";

import { useBuilder } from "@/context/BuilderContext";
import { formatPrice } from "@/lib/utils";
import { cn } from "@/lib/utils";
import { BUILDER_STEPS, BUILDER_BASE_PRICE } from "@/lib/constants";
import { Zap, FileText, Pencil, Scissors } from "lucide-react";
import { useState, useEffect } from "react";
import { useTranslations } from "next-intl";
import { useBuilderOptions } from "@/hooks/useBuilderOptions";

/** Human-readable labels for extras keys */
const EXTRAS_LABELS: Record<string, string> = {
  rush: "Rush Production",
  bangs: "Bangs",
};

export function StepExtras() {
  const {
    selections,
    setOption,
    specialNotes,
    setNotes,
    priceBreakdown,
    totalPrice,
    goToStep,
  } = useBuilder();
  const t = useTranslations("builder");
  const { options } = useBuilderOptions();
  const dbPrices = new Map((options.extras || []).map((o) => [o.value, o.price]));

  const EXTRAS = [
    {
      key: "bangs",
      label: t("bangsLabel"),
      description: t("bangsDesc"),
      price: dbPrices.get("Bangs") ?? 0,
      icon: Scissors,
    },
    {
      key: "rush",
      label: t("rushLabel"),
      description: t("rushDesc"),
      price: dbPrices.get("Rush Production") ?? 350,
      icon: Zap,
    },
  ];

  // Parse the extras selection as a comma-separated list of active keys
  const activeExtras = selections.extras
    ? selections.extras.split(",").filter(Boolean)
    : [];

  const [localToggles, setLocalToggles] = useState<Record<string, boolean>>(() => {
    const initial: Record<string, boolean> = {};
    EXTRAS.forEach((e) => {
      initial[e.key] = activeExtras.includes(e.key);
    });
    return initial;
  });

  // Sync toggles to builder context
  useEffect(() => {
    const activeKeys = Object.entries(localToggles)
      .filter(([, active]) => active)
      .map(([key]) => key);
    const totalExtrasPrice = EXTRAS.filter((e) => localToggles[e.key]).reduce(
      (sum, e) => sum + e.price,
      0
    );
    const value = activeKeys.length > 0 ? activeKeys.join(",") : "";
    setOption("extras", value || "none", totalExtrasPrice);
  }, [localToggles, setOption]);

  const handleToggle = (key: string) => {
    setLocalToggles((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  // Build the summary rows from selections (steps 0-4, skip extras which is the current step)
  const summarySteps = BUILDER_STEPS.slice(0, 5); // base_type, texture, length, color, cap_size

  // Format selected extras for the summary
  const selectedExtrasDisplay = (() => {
    const active = Object.entries(localToggles)
      .filter(([, v]) => v)
      .map(([key]) => EXTRAS_LABELS[key] || key);
    return active.length > 0 ? active.join(", ") : t("noExtras");
  })();

  // Calculate add-on total (non-zero price items)
  const addOnEntries = (
    Object.entries(priceBreakdown) as [string, number][]
  ).filter(([, val]) => val > 0);

  const guides = [
    t("extrasGuideRush"),
  ];

  return (
    <div className="pt-2 space-y-6">
      {/* Contextual Guidance */}
      <div className="rounded-lg border border-brand-dark/5 bg-brand-cream/30 p-4 sm:p-5">
        <ul className="space-y-2">
          {guides.map((guide, idx) => (
            <li key={idx} className="flex items-start gap-2 text-xs leading-relaxed text-foreground/70">
              <span className="mt-1 h-1 w-1 shrink-0 rounded-full bg-brand-gold" />
              {guide}
            </li>
          ))}
        </ul>
      </div>

      {/* Toggle options */}
      <div className="space-y-3">
        {EXTRAS.map((option) => {
          const isActive = localToggles[option.key];
          const Icon = option.icon;

          return (
            <button
              key={option.key}
              onClick={() => handleToggle(option.key)}
              className={cn(
                "w-full flex items-center gap-3 sm:gap-4 p-4 rounded-lg border-2 transition-all duration-300 text-start active:scale-[0.98]",
                isActive
                  ? "border-brand-gold bg-brand-gold/[0.03]"
                  : "border-theme-border bg-surface hover:border-brand-gold-light/50"
              )}
            >
              {/* Icon */}
              <div
                className={cn(
                  "w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0 transition-colors duration-300",
                  isActive
                    ? "bg-brand-gold/10 text-brand-gold"
                    : "bg-brand-cream text-foreground-muted"
                )}
              >
                <Icon className="w-5 h-5" />
              </div>

              {/* Label + description */}
              <div className="flex-1 min-w-0">
                <p className="font-medium text-foreground">{option.label}</p>
                <p className="text-xs text-brand-muted">{option.description}</p>
              </div>

              {/* Price */}
              <span className="text-sm font-medium text-foreground flex-shrink-0 me-3">
                {option.price > 0 ? `+${formatPrice(option.price)}` : t("extrasFree")}
              </span>

              {/* Toggle switch */}
              <div
                className={cn(
                  "w-11 h-6 rounded-full flex-shrink-0 transition-colors duration-300 relative",
                  isActive ? "bg-brand-gold" : "bg-theme-border"
                )}
              >
                <div
                  className={cn(
                    "absolute top-0.5 w-5 h-5 rounded-full bg-white shadow-sm transition-transform duration-300",
                    isActive ? "translate-x-[22px]" : "translate-x-0.5"
                  )}
                />
              </div>
            </button>
          );
        })}
      </div>

      {/* Special notes */}
      <div>
        <label className="flex items-center gap-2 mb-2">
          <FileText className="w-4 h-4 text-brand-muted" />
          <span className="text-sm font-medium text-foreground">
            {t("specialNotes")}
          </span>
          <span className="text-xs text-brand-muted">({t("optional")})</span>
        </label>
        <textarea
          value={specialNotes}
          onChange={(e) => setNotes(e.target.value.slice(0, 500))}
          placeholder={t("specialNotesPlaceholder")}
          rows={3}
          maxLength={500}
          className="w-full px-4 py-3 border border-theme-border rounded-lg text-sm text-foreground placeholder:text-foreground-muted focus:outline-none focus:border-brand-gold focus:ring-1 focus:ring-brand-gold transition-colors resize-none"
        />
        {/* Audit Section 5 — show counter so user knows the 500-char cap. */}
        <div className="mt-1 text-xs text-foreground-muted text-end">
          {specialNotes?.length || 0}/500
        </div>
      </div>

      {/* ─── Divider ──────────────────────────────────────── */}
      <div className="border-t border-foreground/[0.08] pt-6">
        {/* Summary heading */}
        <h3 className="font-display text-lg sm:text-xl text-foreground tracking-tight mb-4">
          {t("yourSummary")}
        </h3>

        {/* Summary card */}
        <div className="rounded-xl bg-brand-cream/40 border border-foreground/[0.06] overflow-hidden animate-fade-up">
          {/* Selection rows — steps 1-5 */}
          <div className="divide-y divide-foreground/[0.05]">
            {summarySteps.map((step, idx) => {
              const key = step.key as keyof typeof selections;
              const raw = selections[key];
              const displayValue = raw
                ? raw.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase())
                : t("noneSelected");

              return (
                <div
                  key={step.key}
                  className="flex items-center justify-between px-4 py-3 group"
                >
                  <div className="flex items-center gap-3 min-w-0">
                    <span className="text-xs font-medium text-brand-gold w-5 text-center flex-shrink-0">
                      {idx + 1}
                    </span>
                    <span className="text-sm text-brand-muted">{step.label}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <span
                      className={cn(
                        "text-sm font-medium truncate max-w-[140px] sm:max-w-[200px]",
                        raw ? "text-foreground" : "text-brand-muted/50"
                      )}
                    >
                      {displayValue}
                    </span>
                    <button
                      onClick={() => goToStep(idx)}
                      className="p-1.5 rounded-md text-brand-muted/40 hover:text-brand-gold hover:bg-brand-gold/10 transition-all duration-200 opacity-0 group-hover:opacity-100 focus:opacity-100"
                      aria-label={`${t("editStep")} ${step.label}`}
                    >
                      <Pencil className="w-3.5 h-3.5" />
                    </button>
                  </div>
                </div>
              );
            })}

            {/* Extras row */}
            <div className="flex items-center justify-between px-4 py-3 group">
              <div className="flex items-center gap-3 min-w-0">
                <span className="text-xs font-medium text-brand-gold w-5 text-center flex-shrink-0">
                  6
                </span>
                <span className="text-sm text-brand-muted">
                  {BUILDER_STEPS[5].label}
                </span>
              </div>
              <span
                className={cn(
                  "text-sm font-medium truncate max-w-[180px] sm:max-w-[240px]",
                  selectedExtrasDisplay !== t("noExtras")
                    ? "text-foreground"
                    : "text-brand-muted/50"
                )}
              >
                {selectedExtrasDisplay}
              </span>
            </div>
          </div>

          {/* ─── Price breakdown ─────────────────────────── */}
          <div className="border-t border-foreground/[0.08] bg-surface/60 px-4 py-4 space-y-2">
            {/* Base price */}
            <div className="flex items-center justify-between text-sm">
              <span className="text-brand-muted">{t("basePriceLabel")}</span>
              <span className="text-foreground">
                {formatPrice(BUILDER_BASE_PRICE)}
              </span>
            </div>

            {/* Add-on line items */}
            {addOnEntries.map(([key, val]) => {
              const stepDef = BUILDER_STEPS.find((s) => s.key === key);
              return (
                <div
                  key={key}
                  className="flex items-center justify-between text-sm"
                >
                  <span className="text-brand-muted">
                    {stepDef?.label ?? key} {t("addOnsLabel").toLowerCase()}
                  </span>
                  <span className="text-foreground">+{formatPrice(val)}</span>
                </div>
              );
            })}

            {/* Divider before total */}
            <div className="border-t border-foreground/[0.06] pt-2 mt-2 flex items-center justify-between">
              <span className="text-sm font-semibold text-foreground">
                {t("totalLabel")}
              </span>
              <span className="font-display text-lg text-foreground tracking-tight">
                {formatPrice(totalPrice)}
              </span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
